
import React, { useState, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { DollarSign } from "lucide-react";
import { Separator } from "@/components/ui/separator";

interface ValueInputProps {
  value: string;
  onChange: (value: string) => void;
  outcome?: string | null;
  estimatedRevenue?: string;
}

const ValueInput: React.FC<ValueInputProps> = ({
  value,
  onChange,
  outcome,
  estimatedRevenue = "$1000"
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const isMobile = useIsMobile();

  const isDisqualified = outcome?.includes('Disqualified') || outcome === 'Existing Customer';
  const isSaleWon = outcome === 'Sale Won';

  useEffect(() => {
    if (!value || value === '$') {
      setDisplayValue('');
      return;
    }

    const numericValue = parseFloat(value.replace(/[$,]/g, ''));
    if (isNaN(numericValue)) {
      setDisplayValue('');
      return;
    }

    const formatted = numericValue.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    
    setDisplayValue(formatted);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    const numericValue = inputValue.replace(/[$,]/g, '');
    
    if (numericValue === '' || /^\d+$/.test(numericValue)) {
      onChange(numericValue);
    }
  };

  if (isDisqualified) {
    return <div className="h-8"></div>;
  }

  return (
    <div className="relative w-full h-8 text-sm">
      <div className={cn(
        "flex items-center w-full h-full rounded-md",
        !isFocused && (
          isMobile 
            ? "border border-gray-200" 
            : "border border-transparent hover:border-gray-200 transition-colors"
        ),
        "bg-white py-1.5"
      )}>
        <DollarSign className="h-4 w-4 text-gray-500 ml-0.5" />
        <Separator orientation="vertical" className="h-4 mx-1" />
        <Input 
          value={displayValue}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className="h-6 w-full min-w-[90px] text-sm border-none shadow-none outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:ring-0 p-0 pl-1 bg-transparent" 
          placeholder={isSaleWon ? estimatedRevenue : "Enter value"}
        />
      </div>
    </div>
  );
};

export default ValueInput;
